import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchWarrantyClaims } from '@/services/warranties';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { WarrantyClaim } from '@/types/models';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { Pagination } from '@/components/common/Pagination';
import { SearchInput } from '@/components/common/SearchInput';
import { WarrantyClaimForm } from '@/features/warranties/WarrantyClaimForm';
import { WarrantyClaimDetail } from '@/features/warranties/WarrantyClaimDetail';
import {
  ShieldCheck,
  Plus,
  ChevronDown,
  CalendarDays,
  User,
  Loader2,
} from 'lucide-react';
import { PAGE_SIZE } from '@/lib/constants';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const WarrantiesPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailClaimId, setDetailClaimId] = useState<string | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.warranties.list({ search, status: statusFilter, page }),
    queryFn: () => fetchWarrantyClaims({ search, status: statusFilter, page }),
    placeholderData: (prev) => prev,
  });

  const claims = data?.data ?? [];
  const totalCount = data?.count ?? 0;

  const handleSearch = (val: string) => {
    setSearch(val);
    setPage(0);
  };

  const handleCreateSuccess = (claimId: string) => {
    setCreateModalOpen(false);
    void refetch();
    setDetailClaimId(claimId);
  };

  return (
    <div className="space-y-5 text-xs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <ShieldCheck className="w-5 h-5 text-brand-green" />
            <span>Warranty Claims Tracker</span>
          </h1>
          <p className="text-brand-muted mt-0.5">
            {isLoading ? 'Loading…' : `${totalCount.toLocaleString('en-PK')} claim${totalCount !== 1 ? 's' : ''} logged`}
          </p>
        </div>
        <button
          onClick={() => setCreateModalOpen(true)}
          className="inline-flex items-center space-x-2 px-4 py-2 bg-brand-green text-white text-sm font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm"
          id="new-warranty-claim-btn"
        >
          <Plus className="w-4 h-4" />
          <span>Open Warranty Claim</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={handleSearch}
            placeholder="Search claim #, issue, or resolution..."
          />
        </div>

        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-brand-border rounded-md text-brand-black focus:outline-none focus:ring-2 focus:ring-brand-green appearance-none"
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="inspection">Under Inspection</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="repairing">Repairing</option>
            <option value="replaced">Replaced</option>
            <option value="closed">Closed</option>
          </select>
          <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-muted" />
        </div>
      </div>

      {/* Error */}
      {isError && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (
        <>
          {claims.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center bg-white border rounded-xl">
              <ShieldCheck className="w-12 h-12 text-brand-muted/30 mb-3" />
              <p className="font-bold text-brand-black">No warranty claims found</p>
              <p className="text-brand-muted mt-1">
                {search || statusFilter
                  ? 'Try adjusting your search or status filter.'
                  : 'Open a claim using the button above when a customer reports a warranty issue.'}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Opened</th>
                      <th className="text-left px-4 py-3">Claim #</th>
                      <th className="text-left px-4 py-3">Laptop Item</th>
                      <th className="text-left px-4 py-3">Customer</th>
                      <th className="text-left px-4 py-3">Issue Summary</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {claims.map((claim: WarrantyClaim) => (
                      <tr
                        key={claim.id}
                        onClick={() => setDetailClaimId(claim.id)}
                        className="hover:bg-brand-bg/60 cursor-pointer transition-colors"
                      >
                        <td className="px-4 py-3 text-brand-muted whitespace-nowrap">
                          <span className="flex items-center space-x-1">
                            <CalendarDays className="w-3.5 h-3.5 text-brand-green shrink-0" />
                            <span>{formatDate(claim.opened_at)}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-brand-black font-mono">
                          #{claim.claim_number}
                        </td>
                        <td className="px-4 py-3 font-semibold text-brand-black">
                          {claim.laptop ? (
                            <span>
                              {claim.laptop.stock_id} — {claim.laptop.brand} {claim.laptop.model}
                            </span>
                          ) : (
                            'Laptop'
                          )}
                        </td>
                        <td className="px-4 py-3 text-brand-muted">
                          <span className="flex items-center space-x-1 font-medium text-brand-black">
                            <User className="w-3.5 h-3.5 text-brand-muted" />
                            <span>{claim.customer?.name ?? 'Walk-in Customer'}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-brand-muted max-w-xs truncate">
                          {claim.issue}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2 py-0.5 text-[10px] font-bold rounded-full uppercase ${
                              claim.status === 'approved' || claim.status === 'replaced'
                                ? 'bg-green-100 text-green-800'
                                : claim.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {claim.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {totalCount > PAGE_SIZE && <Pagination page={page} totalCount={totalCount} onPageChange={setPage} />}
        </>
      )}

      {/* Claim Form Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Open Warranty Claim"
        maxWidth="md"
      >
        <WarrantyClaimForm
          onSuccess={handleCreateSuccess}
          onCancel={() => setCreateModalOpen(false)}
        />
      </Modal>

      {/* Claim Detail Modal */}
      <Modal
        isOpen={!!detailClaimId}
        onClose={() => setDetailClaimId(null)}
        title="Warranty Claim Record"
        maxWidth="lg"
      >
        {detailClaimId && (
          <WarrantyClaimDetail
            claimId={detailClaimId}
            onClose={() => setDetailClaimId(null)}
          />
        )}
      </Modal>
    </div>
  );
};
